import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Stack,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Help as HelpIcon,
  Straighten as RulerIcon,
  CropFree as MeasureIcon,
  TrendingFlat as ArrowIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { EasyCheckDialog, EasyCheckButton } from '../../common/Controls';
import { styled } from '@mui/material/styles';

// Styled components for the demo cheque
const DemoCheque = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '2.5/1', // Typical cheque ratio
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: 4,
  padding: theme.spacing(1),
  backgroundColor: '#f8f9fa',
  overflow: 'hidden'
}));

const MeasurementLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderLeft: `1px dashed ${theme.palette.error.main}`,
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  fontWeight: 600
}));

const MeasurementArrow = styled(Box)(({ theme }) => ({
  position: 'absolute',
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '2px'
}));

const DemoField = styled(Box)(({ theme }) => ({
  position: 'absolute',
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  padding: '2px 4px',
  borderRadius: 2,
  fontSize: '0.7rem',
  fontWeight: 500,
  opacity: 0.8,
  border: `1px solid ${theme.palette.primary.main}`,
  whiteSpace: 'nowrap'
}));

const TemplateHelpModal = ({ open, onClose }) => {
  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="How to Measure Your Cheque Template"
      titleIcon={<HelpIcon />}
      variant="large"
      maxWidth="lg"
      PaperProps={{
        sx: { 
          minHeight: '80vh',
          width: '95vw',
          maxWidth: 1000
        }
      }}
      actions={
        <EasyCheckButton
          buttonType="form"
          variant="contained"
          onClick={onClose}
        >
          Got It!
        </EasyCheckButton>
      }
    >
      <Stack spacing={3}>
        {/* Important Notice */}
        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Important:</strong> All measurements should be in centimeters (cm) from the top-left corner of your cheque.
          </Typography>
          <Typography variant="body2">
            Use a ruler to measure the distance from the left edge and top edge of your physical cheque to each field position.
          </Typography>
        </Alert>

        {/* Demo Cheque Visual */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MeasureIcon color="primary" />
            Demo Cheque Template
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
            The example below shows how to measure the <strong>start of the data field</strong> (not the label):
            <br/>
            <strong style={{ color: '#d32f2f' }}>RED arrow = Left (3.2cm)</strong> (from left edge to start of <u>Beneficiary Name</u>),
            <strong style={{ color: '#2e7d32' }}>GREEN arrow = Top (2.8cm)</strong> (from top edge to start of <u>Beneficiary Name</u>),
            both measured from the <strong style={{ color: '#ed6c02' }}>top-left corner (0,0)</strong>.
          </Typography>
          
          <Box sx={{ position: 'relative', maxWidth: 700, mx: 'auto' }}>
            <DemoCheque elevation={3}>
              {/* Bank Name */}
              <DemoField sx={{ top: '8px', left: '8px' }}>
                SAMPLE BANK
              </DemoField>
              
              {/* Date Field */}
              <Box sx={{ position: 'absolute', top: '25%', right: '10%', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ color: '#888', fontWeight: 400 }}>Date:</span>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.9rem', ml: 1 }}>
                  _______
                </Box>
              </Box>
              
              {/* Pay To Field */}
              <Box sx={{ position: 'absolute', top: '44%', left: '18%', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ color: '#888', fontWeight: 400 }}>Pay to:</span>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.9rem', ml: 1 }}>
                  BENEFICIARY NAME
                </Box>
              </Box>
              
              {/* Amount Field */}
              <Box sx={{ position: 'absolute', top: '40%', right: '8%', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ color: '#888', fontWeight: 400 }}>Amount:</span>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.9rem', ml: 1 }}>
                  1,500.00
                </Box>
              </Box>
              
              {/* Amount in Words Line 1 */}
              <Box sx={{ position: 'absolute', top: '55%', left: '12%', display: 'flex', alignItems: 'center', gap: 1, width: '60%' }}>
                <span style={{ color: '#888', fontWeight: 400 }}>Amount in words:</span>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.9rem', ml: 1, width: '100%' }}>
                  One Thousand Five Hundred Dirhams
                </Box>
              </Box>
              
              {/* Amount in Words Line 2 */}
              <Box sx={{ position: 'absolute', top: '68%', left: '12%', display: 'flex', alignItems: 'center', gap: 1, width: '40%' }}>
                <span style={{ color: '#888', fontWeight: 400 }}>Contd:</span>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.9rem', ml: 1, width: '100%' }}>
                  Only
                </Box>
              </Box>
              
              {/* Signature Line */}
              <Box sx={{ position: 'absolute', bottom: '8px', right: '8px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ color: '#888', fontWeight: 400 }}>Signature:</span>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.9rem', ml: 1 }}>
                  _______
                </Box>
              </Box>
              
              {/* LEFT Measurement Dotted Arrow (Red) */}
              <Box sx={{
                position: 'absolute',
                top: '48%',
                left: 0,
                width: '22%', // width matches left offset of blue box
                height: 0,
                zIndex: 2,
                pointerEvents: 'none',
              }}>
                {/* Dotted line */}
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  top: -1,
                  width: '100%',
                  height: 0,
                  borderTop: '2px dotted #d32f2f',
                  zIndex: 2,
                }} />
                {/* Arrowhead */}
                <Box sx={{
                  position: 'absolute',
                  right: -12,
                  top: -7,
                  width: 0,
                  height: 0,
                  borderTop: '7px solid transparent',
                  borderBottom: '7px solid transparent',
                  borderLeft: '12px solid #d32f2f',
                  zIndex: 3,
                }} />
                {/* Label */}
                <Box sx={{
                  position: 'absolute',
                  left: -110,
                  top: -16,
                  color: '#d32f2f',
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1.5px solid #d32f2f',
                  zIndex: 4,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  pointerEvents: 'auto',
                  minWidth: '90px',
                  textAlign: 'center',
                }}>
                  Left (3.2cm)
                </Box>
              </Box>

              {/* TOP Measurement Dotted Arrow (Green) */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: '22%', // left offset matches blue box
                width: 0,
                height: '48%', // height matches top offset of blue box
                zIndex: 2,
                pointerEvents: 'none',
              }}>
                {/* Dotted line */}
                <Box sx={{
                  position: 'absolute',
                  left: -1,
                  top: 0,
                  width: 0,
                  height: '100%',
                  borderLeft: '2px dotted #2e7d32',
                  zIndex: 2,
                }} />
                {/* Arrowhead */}
                <Box sx={{
                  position: 'absolute',
                  bottom: -12,
                  left: -7,
                  width: 0,
                  height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderTop: '12px solid #2e7d32',
                  zIndex: 3,
                }} />
                {/* Label */}
                <Box sx={{
                  position: 'absolute',
                  top: -38,
                  left: -40,
                  color: '#2e7d32',
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1.5px solid #2e7d32',
                  zIndex: 4,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  pointerEvents: 'auto',
                  minWidth: '90px',
                  textAlign: 'center',
                }}>
                  Top (2.8cm)
                </Box>
              </Box>
            </DemoCheque>
          </Box>
        </Box>

        {/* Measurement Instructions */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RulerIcon color="primary" />
              How to Measure
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: 'error.main' 
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Left Position (cm) - Red Line" 
                  secondary="Horizontal distance from the left edge (0cm) to where the field starts"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: 'success.main' 
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Top Position (cm) - Green Line" 
                  secondary="Vertical distance from the top edge (0cm) down to the field"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: 'warning.main' 
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Reference Point (0,0) - Orange Dot" 
                  secondary="Starting point: top-left corner of your cheque"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.main' 
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Line Width (cm)" 
                  secondary="How wide the text field should be (for amount in words)"
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Measurement Tips
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Use a ruler or measuring tape" 
                  secondary="Measure in centimeters for accuracy"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Start from top-left corner" 
                  secondary="All measurements are from the top-left corner (0,0)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Test print recommended" 
                  secondary="Always test with a sample cheque first"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Account for printer margins" 
                  secondary="Some printers have small margins to consider"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>

        {/* Sample Measurements */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Sample Measurements
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, backgroundColor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
                <Typography variant="subtitle2" color="error.main" gutterBottom sx={{ fontWeight: 600 }}>
                  📍 Beneficiary Name
                </Typography>
                <Typography variant="body2">
                  <strong style={{ color: '#d32f2f' }}>Left: 3.2cm</strong><br/>
                  <strong style={{ color: '#2e7d32' }}>Top: 2.8cm</strong>
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, backgroundColor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom sx={{ fontWeight: 600 }}>
                  📅 Date Field
                </Typography>
                <Typography variant="body2">
                  <strong style={{ color: '#d32f2f' }}>Left: 15.5cm</strong><br/>
                  <strong style={{ color: '#2e7d32' }}>Top: 1.8cm</strong>
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, backgroundColor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ fontWeight: 600 }}>
                  💰 Amount Field
                </Typography>
                <Typography variant="body2">
                  <strong style={{ color: '#d32f2f' }}>Left: 16.2cm</strong><br/>
                  <strong style={{ color: '#2e7d32' }}>Top: 3.5cm</strong>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </EasyCheckDialog>
  );
};

export default TemplateHelpModal; 